import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, Eraser, PenLine, X } from 'lucide-react';

const AUTO_STROKES = [
    [
        [0.1, 0.58],
        [0.15, 0.34],
        [0.22, 0.5],
        [0.2, 0.7],
        [0.3, 0.36],
        [0.38, 0.28],
        [0.42, 0.56],
        [0.4, 0.72],
        [0.5, 0.34],
        [0.58, 0.3],
        [0.63, 0.58],
        [0.72, 0.36],
        [0.8, 0.46],
        [0.9, 0.4],
    ],
    [
        [0.16, 0.78],
        [0.42, 0.74],
        [0.68, 0.76],
        [0.86, 0.7],
        [0.9, 0.62],
    ],
];

const SUCCESS_MESSAGE = 'Ponto registrado com criptografia e geolocalização com sucesso!';

const strokeUntil = (points, progress) => {
    if (!points || points.length < 2 || !Number.isFinite(progress) || progress >= 1) {
        return points;
    }
    const segments = points.length - 1;
    const exact = Math.max(0, progress) * segments;
    const index = Math.min(segments - 1, Math.max(0, Math.floor(exact)));
    const start = points[index];
    const end = points[index + 1];
    if (!start || !end) return points;
    const local = exact - index;
    return [
        ...points.slice(0, index + 1),
        [
            start[0] + (end[0] - start[0]) * local,
            start[1] + (end[1] - start[1]) * local,
        ],
    ];
};

const drawSmoothStroke = (ctx, points, width, height) => {
    if (!points || points.length < 2 || !points[0]) return;
    ctx.beginPath();
    ctx.moveTo(points[0][0] * width, points[0][1] * height);
    for (let index = 1; index < points.length - 1; index += 1) {
        const midX = ((points[index][0] + points[index + 1][0]) / 2) * width;
        const midY = ((points[index][1] + points[index + 1][1]) / 2) * height;
        ctx.quadraticCurveTo(points[index][0] * width, points[index][1] * height, midX, midY);
    }
    const last = points[points.length - 1];
    ctx.lineTo(last[0] * width, last[1] * height);
    ctx.stroke();
};

const readCanvasSize = (canvas) => {
    const rect = canvas.getBoundingClientRect();
    const pixelRatio = window.devicePixelRatio || 1;
    const width = Math.max(1, rect.width);
    const height = Math.max(1, rect.height);
    const bitmapWidth = Math.floor(width * pixelRatio);
    const bitmapHeight = Math.floor(height * pixelRatio);

    if (canvas.width !== bitmapWidth || canvas.height !== bitmapHeight) {
        canvas.width = bitmapWidth;
        canvas.height = bitmapHeight;
    }

    const ctx = canvas.getContext('2d');
    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    return { ctx, width, height };
};

/**
 * Modal com canvas de assinatura digital.
 * Desenha uma assinatura automaticamente e também aceita traço livre.
 *
 * @param {{ open: boolean, onClose: () => void, onRegistered: () => void }} props
 */
const SignatureCanvasModal = ({ open, onClose, onRegistered }) => {
    const titleId = useId();
    const descriptionId = useId();
    const canvasRef = useRef(null);
    const dialogRef = useRef(null);
    const sceneRef = useRef({
        autoCompleted: 0,
        partial: null,
        userStrokes: [],
    });
    const drawingRef = useRef(null);
    const stopAnimationRef = useRef(() => {});
    const generationRef = useRef(0);
    const onCloseRef = useRef(onClose);
    const [replayKey, setReplayKey] = useState(0);
    const [phase, setPhase] = useState('idle');

    onCloseRef.current = onClose;

    const paint = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const { ctx, width, height } = readCanvasSize(canvas);
        const dark = window.matchMedia('(prefers-color-scheme: dark)').matches;

        ctx.clearRect(0, 0, width, height);
        ctx.save();
        ctx.strokeStyle = dark ? 'rgba(148, 163, 184, 0.45)' : 'rgba(100, 116, 139, 0.4)';
        ctx.setLineDash([5, 7]);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(width * 0.08, height * 0.8);
        ctx.lineTo(width * 0.92, height * 0.8);
        ctx.stroke();
        ctx.restore();

        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#10b981';
        ctx.lineWidth = 2.75;

        const scene = sceneRef.current;
        for (let index = 0; index < scene.autoCompleted; index += 1) {
            drawSmoothStroke(ctx, AUTO_STROKES[index], width, height);
        }
        drawSmoothStroke(ctx, scene.partial, width, height);
        scene.userStrokes.forEach((stroke) => drawSmoothStroke(ctx, stroke, width, height));
    };

    const hasInk = () => {
        const scene = sceneRef.current;
        const userHasStroke = scene.userStrokes.some((stroke) => stroke.length > 1);
        return scene.autoCompleted > 0 || (scene.partial && scene.partial.length > 1) || userHasStroke;
    };

    useEffect(() => {
        if (!open) {
            setPhase('idle');
            sceneRef.current = { autoCompleted: 0, partial: null, userStrokes: [] };
            drawingRef.current = null;
            return undefined;
        }

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        dialogRef.current?.focus();

        const onKeyDown = (event) => {
            if (event.key === 'Escape') onCloseRef.current();
        };
        window.addEventListener('keydown', onKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;

        const generation = generationRef.current + 1;
        generationRef.current = generation;
        const animation = { frameId: 0 };
        let stopped = false;
        let finishStroke = () => {};
        const alive = () => !stopped && generationRef.current === generation;
        stopAnimationRef.current = () => {
            stopped = true;
            window.cancelAnimationFrame(animation.frameId);
            finishStroke();
        };

        sceneRef.current = { autoCompleted: 0, partial: null, userStrokes: [] };
        setPhase('signing');

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        const run = async () => {
            if (!canvasRef.current) return;

            if (reducedMotion) {
                sceneRef.current.autoCompleted = AUTO_STROKES.length;
                sceneRef.current.partial = null;
                paint();
                if (alive()) setPhase('ready');
                return;
            }

            const playStroke = (strokeIndex) => new Promise((resolve) => {
                const stroke = AUTO_STROKES[strokeIndex];
                const duration = strokeIndex === 0 ? 1500 : 650;
                const startedAt = performance.now();
                let settled = false;
                const resolveOnce = () => {
                    if (settled) return;
                    settled = true;
                    resolve();
                };
                finishStroke = resolveOnce;

                const tick = () => {
                    if (!alive()) {
                        resolveOnce();
                        return;
                    }
                    const elapsed = Math.max(0, performance.now() - startedAt);
                    const progress = Math.min(1, elapsed / duration);
                    sceneRef.current.autoCompleted = strokeIndex;
                    sceneRef.current.partial = strokeUntil(stroke, progress);
                    paint();
                    if (progress < 1) {
                        animation.frameId = window.requestAnimationFrame(tick);
                    } else {
                        sceneRef.current.autoCompleted = strokeIndex + 1;
                        sceneRef.current.partial = null;
                        paint();
                        resolveOnce();
                    }
                };
                animation.frameId = window.requestAnimationFrame(tick);
            });

            for (let strokeIndex = 0; strokeIndex < AUTO_STROKES.length; strokeIndex += 1) {
                await playStroke(strokeIndex);
                if (!alive()) return;
            }

            if (alive()) setPhase('ready');
        };

        const startId = window.requestAnimationFrame(() => {
            run();
        });

        const canvas = canvasRef.current;
        const observer = new ResizeObserver(() => paint());
        if (canvas) observer.observe(canvas);

        return () => {
            stopped = true;
            generationRef.current += 1;
            window.cancelAnimationFrame(startId);
            window.cancelAnimationFrame(animation.frameId);
            finishStroke();
            observer.disconnect();
        };
    }, [open, replayKey]);

    const pointerToPoint = (event) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width;
        const y = (event.clientY - rect.top) / rect.height;
        return [
            Math.min(1, Math.max(0, x)),
            Math.min(1, Math.max(0, y)),
        ];
    };

    const handlePointerDown = (event) => {
        if (phase === 'success') return;
        stopAnimationRef.current();
        const scene = sceneRef.current;
        const frozen = AUTO_STROKES.slice(0, scene.autoCompleted);
        if (scene.partial && scene.partial.length > 1) frozen.push(scene.partial);
        const stroke = [pointerToPoint(event)];
        sceneRef.current = {
            autoCompleted: 0,
            partial: null,
            userStrokes: [...frozen, ...scene.userStrokes, stroke],
        };
        drawingRef.current = stroke;
        setPhase('ready');
        event.currentTarget.setPointerCapture(event.pointerId);
        paint();
    };

    const handlePointerMove = (event) => {
        if (!drawingRef.current) return;
        drawingRef.current.push(pointerToPoint(event));
        paint();
    };

    const handlePointerUp = () => {
        drawingRef.current = null;
    };

    const handleClear = () => {
        stopAnimationRef.current();
        sceneRef.current = { autoCompleted: 0, partial: null, userStrokes: [] };
        drawingRef.current = null;
        setPhase('ready');
        paint();
    };

    const handleReplay = () => {
        if (phase === 'success') return;
        setReplayKey((current) => current + 1);
    };

    const handleConfirm = () => {
        if (!hasInk()) return;
        stopAnimationRef.current();
        setPhase('success');
        onRegistered();
    };

    return createPortal(
        <AnimatePresence>
            {open && (
                <motion.div
                    className="fixed inset-0 z-[80] flex items-end justify-center sm:items-center sm:p-6"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    <button
                        type="button"
                        aria-label="Fechar assinatura"
                        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
                        onClick={onClose}
                    />

                    <motion.div
                        ref={dialogRef}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={titleId}
                        aria-describedby={descriptionId}
                        tabIndex={-1}
                        initial={{ opacity: 0, y: 28, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.98 }}
                        transition={{ type: 'spring', stiffness: 320, damping: 30 }}
                        className="relative z-10 flex max-h-[92vh] w-full max-w-xl flex-col overflow-hidden rounded-t-3xl border border-slate-200 bg-white shadow-2xl outline-none dark:border-slate-700 dark:bg-slate-900 sm:rounded-3xl"
                    >
                        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4 dark:border-slate-800 sm:px-6">
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-600">
                                    Assinatura digital
                                </p>
                                <h2 id={titleId} className="mt-1 text-lg font-semibold text-slate-900 dark:text-white">
                                    Confirme a jornada do colaborador
                                </h2>
                            </div>
                            <button
                                type="button"
                                onClick={onClose}
                                className="rounded-full p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-slate-800 dark:hover:text-white"
                                aria-label="Fechar"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="overflow-y-auto px-5 py-5 sm:px-6">
                            {phase !== 'success' && (
                                <p id={descriptionId} className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                                    A marcação de demonstração recebe uma assinatura digital. O traço é desenhado
                                    automaticamente e você pode testar o próprio desenho com o dedo ou o mouse.
                                </p>
                            )}

                            {phase === 'success' ? (
                                <motion.div
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-6 text-center dark:border-emerald-500/30 dark:bg-emerald-500/10"
                                >
                                    <motion.div
                                        initial={{ scale: 0.6, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        transition={{ type: 'spring', stiffness: 380, damping: 18 }}
                                        className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500 text-white shadow-lg shadow-emerald-500/30"
                                    >
                                        <CheckCircle2 className="h-7 w-7" />
                                    </motion.div>
                                    <p id={descriptionId} className="mt-4 text-base font-semibold text-emerald-800 dark:text-emerald-200">
                                        {SUCCESS_MESSAGE}
                                    </p>
                                    <p className="mt-2 text-sm text-emerald-700/80 dark:text-emerald-300/80">
                                        Simulação de demonstração. Nenhum ponto real foi gravado.
                                    </p>
                                </motion.div>
                            ) : (
                                <>
                                    <div className="mt-5 overflow-hidden rounded-2xl border border-dashed border-emerald-300 bg-slate-50 dark:border-emerald-500/40 dark:bg-slate-950">
                                        <canvas
                                            ref={canvasRef}
                                            className="h-56 w-full touch-none sm:h-64"
                                            onPointerDown={handlePointerDown}
                                            onPointerMove={handlePointerMove}
                                            onPointerUp={handlePointerUp}
                                            onPointerCancel={handlePointerUp}
                                            aria-label="Área para desenhar a assinatura digital"
                                        />
                                    </div>
                                    <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
                                        {phase === 'ready'
                                            ? 'Assinatura pronta. Desenhe por cima para testar o traço livre.'
                                            : 'Gerando assinatura digital...'}
                                    </p>
                                </>
                            )}
                        </div>

                        <div className="flex flex-col-reverse gap-3 border-t border-slate-100 px-5 py-4 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                            {phase === 'success' ? (
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="inline-flex w-full items-center justify-center rounded-full bg-emerald-500 px-5 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-emerald-600 hover:shadow-emerald-500/20 sm:ml-auto sm:w-auto"
                                >
                                    Concluir
                                </button>
                            ) : (
                                <>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={handleClear}
                                            className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800 sm:flex-none"
                                        >
                                            <Eraser className="h-4 w-4" />
                                            Limpar
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleReplay}
                                            className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800 sm:flex-none"
                                        >
                                            <PenLine className="h-4 w-4" />
                                            Animar
                                        </button>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleConfirm}
                                        disabled={phase !== 'ready' || !hasInk()}
                                        className="inline-flex w-full items-center justify-center rounded-full bg-emerald-500 px-5 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-emerald-600 hover:shadow-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none sm:w-auto"
                                    >
                                        Confirmar registro
                                    </button>
                                </>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>,
        document.body
    );
};

export default SignatureCanvasModal;
